// Client-side replacements for src/lib/admin.functions.ts.
//
// Static SPA build has no server runtime, so createServerFn handlers crash
// (`Cannot destructure 'context' of undefined`). These functions talk to
// Supabase directly from the browser using the authenticated session that
// the supabase-js client already holds. Security is enforced by RLS in
// migration 002 (has_role gate on stories / submissions_raw /
// moderation_notes / user_roles / profiles); the explicit role checks
// below only exist to produce clean UI errors before hitting RLS.
//
// Call shape mirrors the serverFns so consumers can swap
//   const fn = useServerFn(serverFn); fn({ data: { ... } })
// for
//   import { fn } from "@/lib/admin.client"; fn({ data: { ... } })
// with no other changes.

import { supabase } from "@/integrations/supabase/client";

type Role = "admin" | "moderator" | "translator";

type AdminLocale = "en" | "ru" | "fr" | "es" | "zh" | "ar";

async function requireUser(): Promise<{ id: string; email: string }> {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw new Error(error.message);
  const u = data.user;
  if (!u) throw new Error("Not signed in");
  return { id: u.id, email: u.email ?? "" };
}

async function loadRoles(userId: string): Promise<Role[]> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: { role: Role }) => r.role);
}

function canModerate(roles: Role[]): boolean {
  return roles.includes("admin") || roles.includes("moderator");
}

// ---------- Profile ----------

export async function loadAdminProfile() {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  const { data, error } = await supabase
    .from("profiles")
    .select("ui_locale, display_name")
    .eq("id", user.id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return {
    userId: user.id,
    email: user.email,
    roles,
    ui_locale: (data?.ui_locale as string | undefined) ?? "en",
    display_name: (data?.display_name as string | null) ?? null,
  };
}

export async function setAdminUiLocale(args: { data: { ui_locale: AdminLocale } }) {
  const user = await requireUser();
  const { error } = await supabase
    .from("profiles")
    .update({ ui_locale: args.data.ui_locale, updated_at: new Date().toISOString() })
    .eq("id", user.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}

// ---------- Queue ----------

export async function listAdminQueue() {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (roles.length === 0) throw new Error("Forbidden: no staff role");
  const { data, error } = await supabase
    .from("stories")
    .select("id, title, language, submission_type, status, created_at, author_contact")
    .in("status", ["submitted", "in_review", "needs_redaction"])
    .order("created_at", { ascending: true })
    .limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: any) => ({
    id: r.id as string,
    title: r.title as string | null,
    language: r.language as string,
    submission_type: r.submission_type as "victim" | "confessor",
    status: r.status as string,
    created_at: r.created_at as string,
    has_contact: !!r.author_contact,
  }));
}

// ---------- Published originals (entry point for translation review) ----------

export async function listAdminPublishedOriginals() {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (roles.length === 0) throw new Error("Forbidden: no staff role");
  const { data, error } = await supabase
    .from("stories")
    .select("id, title, language, submission_type, status, created_at, author_contact, published_at, canonical_id")
    .eq("status", "published")
    .is("canonical_id", null)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: any) => ({
    id: r.id as string,
    title: r.title as string | null,
    language: r.language as string,
    submission_type: r.submission_type as "victim" | "confessor",
    status: r.status as string,
    created_at: r.created_at as string,
    published_at: r.published_at as string | null,
    has_contact: !!r.author_contact,
  }));
}

// ---------- Story detail ----------

export async function loadAdminStory(args: { data: { id: string } }) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (roles.length === 0) throw new Error("Forbidden: no staff role");

  const { data: story, error: e1 } = await supabase
    .from("stories")
    .select(
      "id, slug, title, title_original, body, language, status, submission_type, author_display, author_contact, consent_flags, category_ids, tags, content_warnings, redaction_log, created_at, canonical_id",
    )
    .eq("id", args.data.id)
    .maybeSingle();
  if (e1) throw new Error(e1.message);
  if (!story) throw new Error("Story not found");

  const { data: raw, error: e2 } = await supabase
    .from("submissions_raw")
    .select("id, raw_body, raw_language, raw_tags, submitted_at")
    .eq("story_id", args.data.id)
    .order("submitted_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (e2) throw new Error(e2.message);

  return {
    story: {
      ...story,
      has_contact: !!story.author_contact,
      // never expose the actual contact value to the UI
      author_contact: undefined,
    },
    raw: raw
      ? {
          raw_body: raw.raw_body as string | null,
          raw_language: raw.raw_language as string | null,
          raw_tags: raw.raw_tags,
          submitted_at: raw.submitted_at as string,
        }
      : null,
    roles,
  };
}

// ---------- Edits ----------

export async function saveAdminStoryEdit(args: {
  data: { id: string; title: string; body: string; reason: string };
}) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { data: prev, error: e0 } = await supabase
    .from("stories")
    .select("body, title, redaction_log")
    .eq("id", args.data.id)
    .maybeSingle();
  if (e0) throw new Error(e0.message);
  if (!prev) throw new Error("Story not found");

  const entry = {
    at: new Date().toISOString(),
    by: user.id,
    what: "body_edit",
    reason: args.data.reason,
    prev_len: (prev.body as string | null)?.length ?? 0,
    new_len: args.data.body.length,
    title_changed: prev.title !== args.data.title,
  };
  const log = Array.isArray(prev.redaction_log) ? prev.redaction_log : [];

  const { error } = await supabase
    .from("stories")
    .update({
      title: args.data.title,
      body: args.data.body,
      redaction_log: [...log, entry],
    })
    .eq("id", args.data.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}

// ---------- Actions ----------

export async function publishAdminStory(args: {
  data: {
    id: string;
    checklist: {
      no_ids: true;
      no_names: true;
      warnings: true;
      facets: true;
      tone: true;
      language_register: true;
      confessor_anonymity: true;
    };
  };
}) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { data: prev, error: e0 } = await supabase
    .from("stories")
    .select("redaction_log")
    .eq("id", args.data.id)
    .maybeSingle();
  if (e0) throw new Error(e0.message);
  const log = Array.isArray(prev?.redaction_log) ? (prev!.redaction_log as any[]) : [];
  const entry = {
    at: new Date().toISOString(),
    by: user.id,
    what: "publish",
    checklist: args.data.checklist,
  };

  const { error } = await supabase
    .from("stories")
    .update({
      status: "published",
      published_at: new Date().toISOString(),
      redaction_log: [...log, entry],
    })
    .eq("id", args.data.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}

export async function rejectAdminStory(args: { data: { id: string; reason: string } }) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { error: e1 } = await supabase
    .from("stories")
    .update({ status: "rejected" })
    .eq("id", args.data.id);
  if (e1) throw new Error(e1.message);
  const { error: e2 } = await supabase
    .from("moderation_notes")
    .insert({ story_id: args.data.id, author_id: user.id, note: `REJECT: ${args.data.reason}` });
  if (e2) throw new Error(e2.message);
  return { ok: true };
}

export async function markNeedsRedaction(args: { data: { id: string; reason: string } }) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { error: e1 } = await supabase
    .from("stories")
    .update({ status: "needs_redaction" })
    .eq("id", args.data.id);
  if (e1) throw new Error(e1.message);
  const { error: e2 } = await supabase
    .from("moderation_notes")
    .insert({
      story_id: args.data.id,
      author_id: user.id,
      note: `NEEDS_REDACTION: ${args.data.reason}`,
    });
  if (e2) throw new Error(e2.message);
  return { ok: true };
}

export async function requestClarification(args: { data: { id: string; note: string } }) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  // Only meaningful when author_contact exists. RLS lets staff read the
  // column; the value is never returned to the UI from loadAdminStory.
  const { data: row, error: e0 } = await supabase
    .from("stories")
    .select("author_contact")
    .eq("id", args.data.id)
    .maybeSingle();
  if (e0) throw new Error(e0.message);
  if (!row || !row.author_contact) {
    throw new Error("This author left no contact — clarification cannot be sent.");
  }

  const { error: e1 } = await supabase
    .from("stories")
    .update({ status: "in_review" })
    .eq("id", args.data.id);
  if (e1) throw new Error(e1.message);
  const { error: e2 } = await supabase
    .from("moderation_notes")
    .insert({
      story_id: args.data.id,
      author_id: user.id,
      note: `CLARIFICATION_REQUESTED: ${args.data.note}`,
    });
  if (e2) throw new Error(e2.message);
  return { ok: true };
}

// ---------- Translations (layer 3) ----------

export async function listStoryTranslations(args: { data: { canonicalId: string } }) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (roles.length === 0) throw new Error("Forbidden: no staff role");

  const { data, error } = await supabase
    .from("stories")
    .select("id, language, status, title, body, created_at")
    .eq("canonical_id", args.data.canonicalId)
    .order("language", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<{
    id: string;
    language: string;
    status: string;
    title: string | null;
    body: string | null;
    created_at: string;
  }>;
}

export async function approveTranslation(args: { data: { id: string } }) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { data: prev, error: e0 } = await supabase
    .from("stories")
    .select("redaction_log, language, canonical_id")
    .eq("id", args.data.id)
    .maybeSingle();
  if (e0) throw new Error(e0.message);
  if (!prev) throw new Error("Translation not found");
  if (!prev.canonical_id) throw new Error("Not a translation (no canonical_id)");

  const log = Array.isArray(prev.redaction_log) ? (prev.redaction_log as any[]) : [];
  const entry = {
    at: new Date().toISOString(),
    by: user.id,
    what: "approve_translation",
    language: prev.language,
  };

  const { error } = await supabase
    .from("stories")
    .update({
      status: "published",
      published_at: new Date().toISOString(),
      redaction_log: [...log, entry],
    })
    .eq("id", args.data.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}

export async function rejectTranslation(args: { data: { id: string; reason: string } }) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { error: e1 } = await supabase
    .from("stories")
    .update({ status: "rejected" })
    .eq("id", args.data.id);
  if (e1) throw new Error(e1.message);
  const { error: e2 } = await supabase
    .from("moderation_notes")
    .insert({
      story_id: args.data.id,
      author_id: user.id,
      note: `REJECT_TRANSLATION: ${args.data.reason}`,
    });
  if (e2) throw new Error(e2.message);
  return { ok: true };
}

// ---------- Categories (source of truth: public.categories) ----------

export async function listCategories() {
  const { data, error } = await supabase
    .from("categories")
    .select("id, slug, name_en, name_ru, name_fr, name_es, name_zh, name_ar, sort_order")
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<{
    id: string;
    slug: string;
    name_en: string | null;
    name_ru: string | null;
    name_fr: string | null;
    name_es: string | null;
    name_zh: string | null;
    name_ar: string | null;
    sort_order: number | null;
  }>;
}

// ---------- Facets edit (metadata; NOT logged to redaction_log) ----------

export async function saveAdminStoryFacets(args: {
  data: { id: string; category_ids: string[]; content_warnings: string[] };
}) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { error } = await supabase
    .from("stories")
    .update({
      category_ids: args.data.category_ids,
      content_warnings: args.data.content_warnings,
    })
    .eq("id", args.data.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}

// ---------- Translation edit (mirrors saveAdminStoryEdit) ----------

export async function saveTranslationEdit(args: {
  data: { id: string; title: string; body: string; reason: string };
}) {
  const user = await requireUser();
  const roles = await loadRoles(user.id);
  if (!canModerate(roles)) throw new Error("Forbidden");

  const { data: prev, error: e0 } = await supabase
    .from("stories")
    .select("body, title, redaction_log, canonical_id, language")
    .eq("id", args.data.id)
    .maybeSingle();
  if (e0) throw new Error(e0.message);
  if (!prev) throw new Error("Translation not found");
  if (!prev.canonical_id) throw new Error("Not a translation (no canonical_id)");

  const entry = {
    at: new Date().toISOString(),
    by: user.id,
    what: "translation_edit",
    language: prev.language,
    reason: args.data.reason,
    prev_len: (prev.body as string | null)?.length ?? 0,
    new_len: args.data.body.length,
    title_changed: prev.title !== args.data.title,
  };
  const log = Array.isArray(prev.redaction_log) ? (prev.redaction_log as any[]) : [];

  const { error } = await supabase
    .from("stories")
    .update({
      title: args.data.title,
      body: args.data.body,
      redaction_log: [...log, entry],
    })
    .eq("id", args.data.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}